import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ViewTableComponent } from '../../base/view-table/view-table.component';
import { FrmManageGiftCertificateComponent } from '../../forms/frm-manage-gift-certificate/frm-manage-gift-certificate.component';
import { RedeemGiftCertificateDialogComponent } from '../../forms/redeem-gift-certificate-dialog/redeem-gift-certificate-dialog.component';
import { PrintService } from 'src/app/services/print.service';
import { ScheduleGiftCertificateEmailDialogComponent } from '../../forms/schedule-gift-certificate-email-dialog/schedule-gift-certificate-email-dialog.component';
import { trigger, state, style, transition, animate } from '@angular/animations';

@Component({
  selector: 'app-gift-certificates',
  templateUrl: './gift-certificates.component.html',
  styleUrls: ['./gift-certificates.component.css'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', overflow: 'hidden', })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class GiftCertificatesComponent extends ViewTableComponent implements OnInit {
  frmManageGiftCertificateComponent = FrmManageGiftCertificateComponent;

  @ViewChild(MatPaginator) override paginator: MatPaginator;
  @ViewChild(MatSort) override sort: MatSort;
  @ViewChild('quickSearchInput') quickSearchInput?: ElementRef<HTMLInputElement>;

  // The page lands on All, so a certificate can be found whatever its
  // status; the other tabs narrow the list.
  links = [
    { label: 'All', path: '/configuration/gift-certificates', queryParams: { status: 'all' } },
    { label: 'Active', path: '/configuration/gift-certificates', queryParams: { status: 'active' } },
    { label: 'Pending', path: '/configuration/gift-certificates', queryParams: { status: 'pending' } },
    { label: 'Redeemed', path: '/configuration/gift-certificates', queryParams: { status: 'redeemed' } },
    { label: 'Expired', path: '/configuration/gift-certificates', queryParams: { status: 'expired' } },
    { label: 'Cancelled', path: '/configuration/gift-certificates', queryParams: { status: 'cancelled' } }
  ];

  activeLink = this.links[0];

  isExpansionDetailRow = (index: number, row: any) => row.hasOwnProperty('expanded');
  expandedElements: { [key: string]: boolean } = {};

  /** Bound to the quick-search input; filters the loaded rows client-side. */
  searchValue: string = '';
  /** The toolbar search is collapsed to an icon until invoked. */
  searchOpen = false;
  /** Row count before filtering, for the "X of Y" meta line. */
  totalCount: number = 0;

  override displayedColumns: string[] = [
    'expand',
    'field_gift_certificate_code',
    'field_recipient_name',
    'field_gift_certificate_amount',
    'field_remaining_balance',
    'field_gift_certificate_status',
    'field_purchase_date',
    'field_expiration_date',
    'email',
    'edit',
  ];

  // Built here rather than injected: this component sits on the BaseComponent
  // constructor chain, and this is how sibling tables obtain the service.
  private _printService: PrintService;

  override ngOnInit() {
    // Hold the loading state across the redirect tick below, so the empty
    // state cannot paint before the first getData() call raises it.
    this.ShowProgressBar = true;
    this.activeLink = this.links[0];
    this._printService = new PrintService(this._drupalRESTService, this._authService);

    this._activatedRoute.queryParams.subscribe(params => {
      const status = params['status'];

      // Landing without a status defaults to the All tab, so the URL, the
      // highlighted tab, and the loaded rows always agree.
      if (!status) {
        this._router.navigate([], {
          relativeTo: this._activatedRoute,
          queryParams: { status: 'all' },
          replaceUrl: true,
        });
        return;
      }

      this.activeLink = this.links.find(link => link.queryParams.status === status) || this.links[0];

      // The parameter name has to match the view's exposed filter identifier,
      // which is what getData() forwards to the REST export. The filter is
      // optional on the view, so leaving it out returns every status; the
      // REST display has no pager, so "All" still arrives in one response.
      this.getData(status === 'all' ? [] : [{ parameter: 'field_gift_certificate_status', value: status }]);
    });
  }

  override getData(params: { parameter: string; value: string; }[]) {
    this.ShowProgressBar = true;
    this.queryParams = params;

    const filters = '?sort_order=ASC&sort_by=field_purchase_date_value';
    const endpoint: string = "/api_rest/v1/gift_certificates" + filters;

    // params are appended as query string values by the REST service, so the
    // status filter is passed through here rather than concatenated above.
    this._drupalRESTService.httpGET(endpoint, params)
      .subscribe(
        (data) => {
          this.ShowProgressBar = false;
          const results = data?.['results'] ?? [];
          results.forEach((element: any) => element.expanded = false);
          this.totalCount = results.length;
          this.dataSource = new MatTableDataSource(results);

          // Staff look certificates up for customers who lost the email, so
          // the quick search matches people as well as the code itself.
          this.dataSource.filterPredicate = (row: any, filter: string) => {
            const haystack = [
              row.field_gift_certificate_code,
              row.field_recipient_name,
              row.field_recipient_email,
              row.field_purchaser_name,
              row.field_purchaser_email,
              row.field_gift_certificate_status,
            ].join(' ').toLowerCase();
            return haystack.includes(filter);
          };
          if (this.searchValue) {
            this.dataSource.filter = this.searchValue.trim().toLowerCase();
          }

          // Amounts arrive as strings and dates as ISO strings; without this
          // the amount columns sort lexicographically ("9.00" > "100.00").
          this.dataSource.sortingDataAccessor = (row: any, column: string) => {
            switch (column) {
              case 'field_gift_certificate_amount':
              case 'field_remaining_balance':
                return Number(row[column]) || 0;
              case 'field_purchase_date':
              case 'field_expiration_date':
                return new Date(row[column]).getTime() || 0;
              default:
                return (row[column] ?? '').toString().toLowerCase();
            }
          };

          // Paging and sorting are client-side: a studio's certificate list is
          // small, and the endpoint has no count/offset support.
          setTimeout(() => {
            this.dataSource.paginator = this.paginator;
            this.dataSource.sort = this.sort;
          });

          this.loadEmailStatus(results);
        },
        error => this.handleError(error)
      );
  }

  /**
   * Deliveries live in the notifications log, not on the certificate, so the
   * last sent date is fetched once per load and merged into the rows.
   */
  private loadEmailStatus(rows: any[]) {
    this._drupalRESTService.httpGET('/api_rest/v1/getGiftCertificateEmailStatus')
      .subscribe((sent: any) => {
        rows.forEach(row => row.email_sent_date = sent?.[row.id] || null);
      });
    this._drupalRESTService.httpGET('/api_rest/v1/getGiftCertificateSmsStatus')
      .subscribe((sent: any) => {
        rows.forEach(row => row.sms_sent_date = sent?.[row.id] || null);
      });
  }

  /** "Sent 09/01/2026", "Scheduled 10/14/2026", or "Not sent". */
  emailStatus(element: any): string {
    if (element.email_sent_date) {
      return 'Sent ' + this.reportDateService.formatDate(element.email_sent_date);
    }
    if (element.field_email_send_date) {
      return 'Scheduled ' + this.reportDateService.formatDate(element.field_email_send_date);
    }
    return 'Not sent';
  }

  /** "Sent 09/01/2026" or "Not sent"; texts are never scheduled. */
  smsStatus(element: any): string {
    return element.sms_sent_date
      ? 'Sent ' + this.reportDateService.formatDate(element.sms_sent_date)
      : 'Not sent';
  }

  applyQuickFilter(value: string) {
    this.searchValue = value;
    if (this.dataSource) {
      this.dataSource.filter = (value || '').trim().toLowerCase();
      if (this.dataSource.paginator) {
        this.dataSource.paginator.firstPage();
      }
    }
  }

  openSearch() {
    this.searchOpen = true;
    // Focus once the input has actually been rendered.
    setTimeout(() => this.quickSearchInput?.nativeElement.focus());
  }

  /** Closes the search and clears any active filter with it. */
  closeSearch() {
    this.searchOpen = false;
    this.applyQuickFilter('');
  }

  clearQuickFilter() {
    this.applyQuickFilter('');
  }

  refreshView() {
    this.getData(this.queryParams);
  }

  /**
   * The single redemption entry point: staff enter the code the customer
   * presents, or arrive from a row's action menu with the code already known.
   * Works for certificates sold here and at other studios alike; the server
   * decides what the certificate can still do.
   */
  openRedeemDialog(code?: string) {
    this._dialogService.openDialog(RedeemGiftCertificateDialogComponent, "defaultXL+", { code: code || '' })
      .afterClosed()
      .subscribe(() => {
        this.getData(this.queryParams);
      });
  }

  openGiftCertificateDialog(id: string, action: string) {
    // The third argument is the dialog's data payload, not a config override:
    // the width/height object previously passed here was silently discarded and
    // left { id, action } unreachable by the form.
    this._dialogService.openDialog(this.frmManageGiftCertificateComponent, "defaultXL+", { id, action })
      .afterClosed()
      .subscribe(() => {
        this.getData(this.queryParams);
      });
  }

  /**
   * Cancels a certificate: it stops being redeemable and the refund owed for
   * its remaining balance is recorded server-side. Record-only: the money
   * itself is returned outside the system.
   */
  cancelGiftCertificate(element: any) {
    const remaining = element.field_remaining_balance ?? element.field_gift_certificate_amount;
    if (!confirm(`Cancel gift certificate ${element.field_gift_certificate_code}? ` +
        `A refund of ${remaining} will be recorded as owed to the purchaser.`)) {
      return;
    }

    this._drupalRESTService.httpPATCH('/api_rest/v1/cancelGiftCertificate', {
      code: element.field_gift_certificate_code,
    }).subscribe(
      (response: any) => {
        const body = response?.body ?? response;
        if (body?.status === 'success') {
          this._snackBar.open(`Gift certificate cancelled. Refund owed: ${body.refund_amount}.`, 'Close', { duration: 6000 });
          this.getData(this.queryParams);
        } else {
          this._snackBar.open(body?.message || 'Could not cancel the gift certificate.', 'Close', { duration: 6000 });
        }
      },
      () => this._snackBar.open('Could not cancel the gift certificate.', 'Close', { duration: 6000 })
    );
  }

  /**
   * Prints the certificate: the same document the email sends. The window is
   * opened synchronously in the click handler (a fetch callback is not a
   * user gesture and would be popup-blocked), then filled once the HTML
   * arrives.
   */
  printGiftCertificate(element: any) {
    const win = this._printService.openPrintWindow();
    if (!win) {
      this._snackBar.open('The print window was blocked. Allow pop-ups for this site and try again.', 'Close', { duration: 6000 });
      return;
    }

    this._drupalRESTService.httpGET(`/api/v1/gift-certificates/${element.id}/print`)
      .subscribe(
        (response: any) => {
          if (!response?.html) {
            win.close();
            this._snackBar.open(response?.error || 'Could not build the printable certificate.', 'Close', { duration: 6000 });
            return;
          }
          this._printService.printDocument(response.html, win);
        },
        () => {
          win.close();
          this._snackBar.open('Could not build the printable certificate.', 'Close', { duration: 6000 });
        }
      );
  }

  /** Sets, changes or clears the date the certificate email goes out. */
  openScheduleDialog(element: any) {
    this._dialogService.openDialog(ScheduleGiftCertificateEmailDialogComponent, 'default', { data: { certificate: element } })
      .afterClosed()
      .subscribe((result) => {
        if (result?.saved) {
          this.getData(this.queryParams);
        }
      });
  }

  /** Resends the gift certificate email to the recipient. */
  resendGiftCertificateEmail(element: any) {
    this._drupalRESTService.httpGET(`/api_rest/v1/sendGiftCertificateEmail?giftCertificateID=${element.id}`)
      .subscribe(
        (response: any) => {
          if (response?.error) {
            this._snackBar.open(response.error, 'Close', { duration: 6000 });
          } else {
            this._snackBar.open('Gift certificate email sent.', 'Close', { duration: 4000 });
            // The Email column reads the delivery log, so reload to show "Sent".
            this.getData(this.queryParams);
          }
        },
        () => this._snackBar.open('Could not send the gift certificate email.', 'Close', { duration: 6000 })
      );
  }

  /** Texts the certificate to the recipient's phone. */
  sendGiftCertificateSms(element: any) {
    this._drupalRESTService.httpGET(`/api_rest/v1/sendGiftCertificateSMS?giftCertificateID=${element.id}`)
      .subscribe(
        (response: any) => {
          if (response?.error) {
            this._snackBar.open(response.error, 'Close', { duration: 6000 });
          } else {
            this._snackBar.open('Gift certificate text message sent.', 'Close', { duration: 4000 });
            this.getData(this.queryParams);
          }
        },
        () => this._snackBar.open('Could not send the gift certificate text message.', 'Close', { duration: 6000 })
      );
  }

  /**
   * Hard-deletes a certificate. Destructive: the delete hook also removes the
   * purchase payment, so this stays admin-gated; Cancel is the normal path.
   */
  deleteGiftCertificate(element: any) {
    if (!confirm(`Permanently delete gift certificate ${element.field_gift_certificate_code}? ` +
        `This also deletes its purchase payment record. Use Cancel instead unless the certificate was created in error.`)) {
      return;
    }

    this._entityRESTService.deleteEntity('gift_certificates', 'gift_certificate', element.id)
      .subscribe(
        () => {
          this._snackBar.open('Gift certificate deleted.', 'Close', { duration: 4000 });
          this.getData(this.queryParams);
        },
        () => this._snackBar.open('Could not delete the gift certificate.', 'Close', { duration: 6000 })
      );
  }

  toggleRow(element: any) {
    this.expandedElements[element.id] = !this.expandedElements[element.id];
  }

  /**
   * Inline text editing in the expanded row, for the staff notes and the
   * gift message. Keyed by row id and field so a second row can open while
   * another is still saving.
   */
  editing: { [key: string]: { value: string; saving: boolean } } = {};

  private editKey(element: any, field: string): string {
    return `${element.id}:${field}`;
  }

  isEditing(element: any, field: string): boolean {
    return !!this.editing[this.editKey(element, field)];
  }

  isSaving(element: any, field: string): boolean {
    return !!this.editing[this.editKey(element, field)]?.saving;
  }

  canEditText(): boolean {
    return this._permissionsService.canAccess('gift_certificates_edit');
  }

  startEdit(element: any, field: string) {
    if (!this.canEditText()) {
      return;
    }
    this.editing[this.editKey(element, field)] = { value: element[field] || '', saving: false };
    // Focus once the textarea has rendered, so the click lands in the field.
    setTimeout(() => {
      const el = document.getElementById(`gc-edit-${element.id}-${field}`) as HTMLTextAreaElement | null;
      if (el) {
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
      }
    });
  }

  cancelEdit(element: any, field: string) {
    delete this.editing[this.editKey(element, field)];
  }

  /** Saves one text field; the row is updated in place rather than reloaded. */
  saveEdit(element: any, field: string) {
    const key = this.editKey(element, field);
    const edit = this.editing[key];
    if (!edit || edit.saving) {
      return;
    }
    const value = edit.value.trim();
    if (value === (element[field] || '')) {
      this.cancelEdit(element, field);
      return;
    }

    edit.saving = true;
    this._entityRESTService.patchEntity('gift_certificates', 'gift_certificate', element.id, { [field]: value })
      .subscribe(
        () => {
          element[field] = value;
          delete this.editing[key];
          this._snackBar.open(field === 'field_gift_message' ? 'Gift message saved.' : 'Notes saved.', 'Close', { duration: 4000 });
        },
        () => {
          edit.saving = false;
          this._snackBar.open('Could not save the change.', 'Close', { duration: 6000 });
        }
      );
  }

  /** Enter saves, Shift+Enter keeps a line break, Escape cancels. */
  onEditKeydown(event: KeyboardEvent, element: any, field: string) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelEdit(element, field);
    } else if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.saveEdit(element, field);
    }
  }
}
